import { sp, IFieldInfo, IRegionalSettingsInfo, IViewInfo } from "@pnp/sp/presets/all";
import { sharepoint } from "../config/constants";
import { parseString } from "xml2js";
import { IMetadataInfo } from "../interfaces/IMetadataInfo";
import { removeDuplicates } from "../components/helpers/collectionHelpers";
import { logger } from "../../../utilities/logger";
import { normalizeMultiValueFieldType } from "../config/sharepointConstants";

export class SharepointSpServices {
  constructor(private webUrl: string, private accessToken: string) {
    sp.setup({
      ie11: true,
      sp: {
        headers: {
          Accept: "application/json;odata=verbose",
          Authorization: `Bearer ${accessToken}`,
        },
        baseUrl: webUrl,
      },
    });
  }

  setUpSp(accessToken: string, webUrl: string) {
    sp.setup({
      ie11: true,
      sp: {
        headers: {
          Accept: "application/json;odata=verbose",
          Authorization: `Bearer ${accessToken}`,
        },
        baseUrl: webUrl,
      },
    });
  }

  getDocumentLibraryEnableFolderCreationSetting = async (sharePointListId: string): Promise<boolean> => {
    try {
      const result = await sp.web.lists.getById(sharePointListId).select("EnableFolderCreation")();
      return result.EnableFolderCreation;
    } catch (err) {
      logger(err, "SharePointSptService.ts (getDocumentLibraryEnableFolderCreationSetting)");
      return false;
    }
  };

  getSiteRegionalSettings = async (): Promise<IRegionalSettingsInfo> => {
    try {
      return await sp.web.regionalSettings.get();
    } catch (error) {
      logger(error, "SharePointService.ts (getSiteRegionalSettings)");
      return null;
    }
  };

  getDocumentLibraryMetadataFields = async (documentLibrary: string): Promise<IMetadataInfo[]> => {
    try {
      const contentTypes = await sp.web.lists.getById(documentLibrary).contentTypes.select("Id").get();
      const result = await Promise.all(
        contentTypes.map(async (item) => {
          this.setUpSp(this.accessToken, this.webUrl);
          let res = await sp.web.lists
            .getById(documentLibrary)
            .contentTypes.getById(item.Id.StringValue)
            .fields.filter(sharepoint.contentTypeFiltering)
            .select("SchemaXml")
            .get();
          if (!res.length) {
            return [];
          }

          return res.map(this.convertMetadataToFields);
        })
      );

      const metadataWithDuplicates = result.reduce((aggregates, current) => [...aggregates, ...current], []);
      return removeDuplicates(metadataWithDuplicates, "Description");
    } catch (err) {
      logger(err, "SharePointSptService.ts (getDocumentLibraryMetadataFields)");
      return [];
    }
  };

  private convertMetadataToFields = (item): IMetadataInfo => {
    let xmlData = {};
    parseString(item.SchemaXml, (err, result) => {
      if (result) {
        xmlData = result.Field.$;
      }
    });

    normalizeMultiValueFieldType(xmlData);

    return {
      Type: xmlData["Type"],
      Description: xmlData["Name"],
    };
  };

  getTaxonomyTermSet = async (sharepointListId: string, internalName: string): Promise<IFieldInfo> => {
    try {
      return await sp.web.lists
        .getById(sharepointListId)
        .fields.getByInternalNameOrTitle(internalName)
        .select("TermSetId,AnchorId")
        .get();
    } catch (err) {
      logger(err, "SharePointSptService.ts (getTaxonomyTerms)");
      return null;
    }
  };

  getSharePointView = async (sharepointListId: string) => {
    const views: IViewInfo[] = await sp.web.lists
      .getById(sharepointListId)
      .views.select("Id,Title,DefaultView,ListViewXml")
      .filter("Hidden eq false")
      .get();
    return views;
  };
}

